import type { FC, ReactNode } from 'react'
import type { InfoItem } from '@/types'

import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'

export type Tone = 'bone' | 'paper' | 'sand' | 'forest' | 'pine' | 'charcoal'

export const toneClasses: Record<Tone, string> = {
    bone: 'bg-bone text-charcoal',
    paper: 'bg-paper text-charcoal',
    sand: 'bg-sand text-charcoal',
    forest: 'bg-forest text-bone',
    pine: 'bg-pine text-bone',
    charcoal: 'bg-charcoal text-bone'
}

export const isDark = (tone: Tone): boolean => tone === 'forest' || tone === 'pine' || tone === 'charcoal'

interface InfoGridProps {
    id: string
    index: string
    label: string
    heading: ReactNode
    intro?: string
    items: InfoItem[]
    tone?: Tone
    columns?: 2 | 3 | 4
    /** Show large step numbers instead of small indexes. */
    steps?: boolean
    children?: ReactNode
}

const columnClasses = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4'
}

/** A headed chapter of numbered cards: practical details, benefits, steps or principles. */
export const InfoGrid: FC<InfoGridProps> = ({
    id,
    index,
    label,
    heading,
    intro,
    items,
    tone = 'bone',
    columns = 3,
    steps = false,
    children
}): ReactNode => {
    const dark = isDark(tone)

    return (
        <section id={id} aria-labelledby={`${id}-heading`} className={cn('relative z-10', toneClasses[tone])}>
            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-10 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index={index} className={dark ? 'text-bone/70' : 'text-charcoal/70'}>
                            {label}
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id={`${id}-heading`}
                            className='mt-6 text-[clamp(2.1rem,4.2vw,4.2rem)] font-light leading-[1.02] tracking-[-0.03em] [&_em]:font-serif [&_em]:font-normal'
                        >
                            {heading}
                        </SplitReveal>
                    </div>

                    {intro && (
                        <p data-reveal className={cn('self-end text-base leading-relaxed lg:col-span-4 lg:col-start-9', dark ? 'text-bone/70' : 'text-charcoal/70')}>
                            {intro}
                        </p>
                    )}
                </div>

                <ol className={cn('mt-14 grid gap-x-8 gap-y-12 lg:mt-20', columnClasses[columns])}>
                    {items.map((item, itemIndex) => (
                        <li
                            key={item.title}
                            data-reveal
                            className={cn('flex flex-col border-t pt-6', dark ? 'border-bone/15' : 'border-charcoal/15')}
                        >
                            {steps ? (
                                <span className={cn('font-serif text-6xl leading-none', dark ? 'text-ochre' : 'text-forest')}>
                                    {pad(itemIndex + 1)}
                                </span>
                            ) : (
                                <span className={cn('font-mono text-[11px] tabular-nums', dark ? 'text-bone/45' : 'text-charcoal/45')}>
                                    {pad(itemIndex + 1)}
                                </span>
                            )}

                            <h3 className={cn('type-wide text-[17px] font-medium uppercase leading-snug tracking-[0.02em]', steps ? 'mt-6' : 'mt-5')}>
                                {item.title}
                            </h3>

                            <p className={cn('mt-3 max-w-sm text-[15px] leading-relaxed', dark ? 'text-bone/70' : 'text-charcoal/70')}>
                                {item.description}
                            </p>

                            {item.meta && (
                                <p className={cn('mt-5 font-mono text-[10px] uppercase tracking-[0.2em]', dark ? 'text-ochre' : 'text-clay')}>
                                    {item.meta}
                                </p>
                            )}
                        </li>
                    ))}
                </ol>

                {children}
            </div>
        </section>
    )
}
